import { useTranslation } from 'react-i18next'
import { ModalPortal } from '../../../../common/components/Portal/Portal'
import { CloseButton } from '../../../../common/components/button/CloseButton'
import { getIcon } from '../../../../common/components/icons/utils'
import { useFadeAnimation } from '../../../../hooks/FadeAnimation/useFadeAnimation'
import { useDisableBodyScroll } from '../../../../hooks/useDisableBodyScroll'
import { useTrapFocus } from '../../../../hooks/useTrapFocus'
import styles from './AddMembersModal.module.css'
import { useCallback, useState } from 'react'
import { ThreeDots } from '../../../../common/components/loaders/ThreeDots/ThreeDots'
import { LoaderWithOverlay } from '../../../../composites/GamesOverview/components/LoaderWithOverlay'
import classNames from 'classnames'
import { AddPlayers } from './AddPlayers'
import { AddPlayersPreview } from './AddPlayersPreview'
import { csvFileInfo, useBusinessSettings } from '../../../../contexts/BusinessSettingsContext'

export interface Player {
  name: string
  email: string
  group?: string
  tag?: number
}

type AddPlayersModalProps = {
  organizationName: string
  onSubmit: (players: Player[]) => Promise<boolean>
  onClose: () => void
  playerCount: number
  maxPlayers: number
  limitPlayers: boolean
}

export type AddPlayersFormValues = {
  players: Player[]
}

export const AddPlayersModal: React.FC<AddPlayersModalProps> = ({
  onSubmit,
  organizationName,
  onClose,
  playerCount,
  maxPlayers,
  limitPlayers,
}) => {
  const { t } = useTranslation()
  const trapFocusRef = useTrapFocus<HTMLDivElement>()
  useDisableBodyScroll()
  const [values, setValues] = useState<AddPlayersFormValues | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [csvValues, setCsvValues] = useState<any>(null)
  const originalMessage = t(
    'settings_organization.players.invite_email.default.message',
    'Hi,<br><br>you have been created a player account in Seppo. Welcome!<br><br>You need to confirm your account. You can do that by clicking the link below:<br>%custom_link<br><br>This invitation is valid for 72 hours.<br><br>You can log in to your account at https://play.seppo.io -> Sign in with player ID.<br><br>User name: your email<br>Password: %password<br><br>Player account grants you easy access to your past games under one login. Games that you have joined from your account will be saved. You can easily go back to them from your dashboard.<br><br>If you did not create the account yourself, it is possible that your instructor has created it. Please ask your instructor for more information.<br><br>Best,<br>Seppo team',
  )

  const { fadeTransition, closeWithDelay } = useFadeAnimation()
  const { checkPlayerAccountsCSV, addPlayerAccountsCSV, getBusinessPlayerAccountInfo } = useBusinessSettings()

  const onSubmitInternal = async () => {
    setSubmitting(true)
    if (csvValues) {
      const isSuccess = await addPlayerAccountsCSV(csvValues)
      if (isSuccess) {
        await getBusinessPlayerAccountInfo()
        setCsvValues(null)
        onCloseInternal()
      }
    } else if (values) {
      const isSuccess = await onSubmit(values?.players)
      if (isSuccess) {
        onCloseInternal()
      }
    }
    setSubmitting(false)
  }

  const onCloseInternal = () => {
    closeWithDelay(350, onClose)
  }

  const onPreview = () => {
    setShowPreview(true)
  }

  const onBack = () => {
    setShowPreview(false)
  }

  const handleOnSetCSVValues = useCallback(
    async (inputFile: csvFileInfo): Promise<boolean> => {
      const response = await checkPlayerAccountsCSV(inputFile)
      if (response.success) setCsvValues(response.value)
      return response
    },
    [checkPlayerAccountsCSV],
  )

  return (
    <ModalPortal>
      <div className={fadeTransition}>
        <div className={styles.backdrop}>
          <div className={classNames(styles.container)} ref={trapFocusRef}>
            <div className={styles.content}>
              {submitting && (
                <LoaderWithOverlay>
                  <ThreeDots />
                </LoaderWithOverlay>
              )}
              <div className={styles.header}>
                <div className={styles.headerTitle}>
                  <span>{getIcon('settingsGradient')}</span>
                  <span className='medium'>
                    {t('settings_organization.players.add_players.title', {
                      organization_name: organizationName,
                      defaultValue: "Add player accounts to '%{organization_name}' organization",
                    })}
                  </span>
                </div>
                <CloseButton autoFocus onClick={onCloseInternal} />
              </div>
              {!showPreview && (
                <AddPlayers
                  onPreview={onPreview}
                  onClose={onCloseInternal}
                  values={values}
                  setAllValues={setValues}
                  setCSVValues={handleOnSetCSVValues}
                  playerCount={playerCount + (values ? values.players.length : 1)}
                  maxPlayers={maxPlayers}
                  limitPlayers={limitPlayers}
                />
              )}
              {showPreview && (
                <AddPlayersPreview
                  onSubmit={onSubmitInternal}
                  onClose={onCloseInternal}
                  onBack={onBack}
                  message={originalMessage}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  )
}
